// ---------------------------------------------------------------------------
// Homefront v3 — structure-kill scoring
// ---------------------------------------------------------------------------
//
// The entire scoring system. Each player's Grid fields its structures; the
// first player to have STRUCTURES_TO_WIN of them FULLY destroyed loses the
// war. A structure is destroyed only when every subplot of its footprint is a
// crater (subplotGrid.isStructureDestroyed) — partial coverage leaves it
// damaged and standing. Cells destroyed count for nothing on their own: a
// crater on empty land only stamps distance intel, a crater that clips a
// structure only damages it on the map. Broods aren't structures and never
// count. Structure level is irrelevant. There is no round limit; both players losing their last
// countable structure in the same round is a draw.
//
// The only synced scoring state is each player's count of structures LOST
// (p1StructuresLost / p2StructuresLost). Both clients derive it from the same
// submitted round: applyCraterDamage reports `structureKills` per crater and
// the loser's count grows by that length, so nothing needs cross-client sync.

import { rules } from './config.js'
import { isStructureDestroyed } from './subplotGrid.js'

// The structures that count toward the kill bar: every structure on the Hive.
export function countableStructures(homefront) {
    return Object.values(homefront?.structures ?? {}).filter(Boolean)
}

// How many countable structures on this Grid are fully destroyed — the count
// a player's `structuresLost` should equal, derived straight from the layout.
export function structuresLost(homefront) {
    return countableStructures(homefront).filter(st => isStructureDestroyed(homefront, st.id)).length
}

// Adds a crater's kills (applyCraterDamage's `structureKills`) to a count.
export function applyKills(lost, kills) {
    return (lost || 0) + (kills?.length ?? kills ?? 0)
}

// A player has lost once STRUCTURES_TO_WIN of their structures are gone.
export function isWarLost(lost) {
    return (lost || 0) >= rules().structuresToWin
}

// War outcome from both players' structures-lost counts. Returns WIN when the
// opponent has lost enough, LOSS when self has, TIE when both crossed the line
// in the same round, or over:false while the war continues. selfLost/oppLost
// are each player's OWN structures lost.
export function warOutcome(selfLost, oppLost) {
    const selfDown = isWarLost(selfLost)
    const oppDown  = isWarLost(oppLost)
    if (selfDown && oppDown) return { over: true, result: 'TIE' }
    if (oppDown)  return { over: true, result: 'WIN' }
    if (selfDown) return { over: true, result: 'LOSS' }
    return { over: false, result: null }
}

// Setup gate: a war is decided by STRUCTURES_TO_WIN kills, so a Grid fielding
// fewer countable structures can't be fought over. Throws (fails loudly) rather
// than clamping the threshold. Returns the count when the Grid passes.
export function assertWarGrid(homefront, label = 'Grid') {
    const n = countableStructures(homefront).length
    const need = rules().structuresToWin
    if (n < need) {
        throw new Error(
            `${label} fields ${n} structure${n === 1 ? '' : 's'}; a war needs at least ${need}.`
        )
    }
    return n
}
